import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Plus, Send, RefreshCw, Trash2, ExternalLink, X, CalendarClock, Film, List, CalendarDays, Archive, ChevronDown } from 'lucide-react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { Card, EmptyState, ListSkeleton } from '../components/ui';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import ClientePicker from '../components/ClientePicker';
import { useClienteAttivo, ordinaClienti, clienteValido } from '../hooks/useClienteAttivo';
import MediaManager from '../components/MediaManager';
import PedCalendario from '../components/PedCalendario';
import { PostingBadge, PostingGuardrail } from '../components/PostingGuardrail';
import { useAuth } from '../context/AuthContext';
import { can, RUOLI_SOCIAL } from '../lib/ruoli';

// Mesi (futuri) per la navigazione del calendario in "In programma".
const CAL_MESI = [
  { k: 'corrente',      label: 'Questo mese' },
  { k: 'mese_prossimo', label: 'Mese prossimo' },
  { k: 'mese_dopo',     label: 'Tra due mesi' },
];
const MESI_ARCH = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

const hhmm = (t) => (t ? String(t).slice(0, 5) : '');
const fmtData = (d) => {
  if (!d) return 'Senza data';
  return new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
};

export default function PedStorie() {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { user } = useAuth();
  const puoPubblicare = can(user, RUOLI_SOCIAL);
  const [clienti, setClienti] = useState([]);
  const [cliente, setCliente] = useClienteAttivo();
  const [orizzonte, setOrizzonte] = useState('corrente'); // mese del calendario (In programma)
  const [vista, setVista] = useState('lista');           // 'lista' | 'calendario'
  const [sezione, setSezione] = useState('programma');    // 'programma' (future) | 'archivio' (passate)
  const [mesiChiusi, setMesiChiusi] = useState(() => new Set());
  const [righe, setRighe] = useState([]);
  const [loading, setLoading] = useState(false);

  // creazione rapida
  const [nuovaData, setNuovaData] = useState('');
  const [nuovaOra, setNuovaOra] = useState('18:00');
  const [nuovoTitolo, setNuovoTitolo] = useState('');
  const [nuovaModalita, setNuovaModalita] = useState('Auto');
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // gestione media (modale per riga)
  const [mediaFor, setMediaFor] = useState(null);      // storia con il gestore media aperto
  const [filtroPosting, setFiltroPosting] = useState(''); // filtro guardrail per stato_posting
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    api.get('/clienti').then(r => {
      const lista = ordinaClienti(r.data || []);
      setClienti(lista);
      setCliente(clienteValido(lista, cliente));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carichiamo TUTTE le storie del cliente e partizioniamo in JS (come il PED).
  const load = () => {
    if (!cliente) { setRighe([]); return; }
    setLoading(true);
    api.get(`/ped?cliente_id=${cliente}&formato=Storia`)
      .then(r => setRighe(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [cliente]); // eslint-disable-line react-hooks/exhaustive-deps
  // Aggiornamento automatico silenzioso (scheda visibile)
  useLiveRefresh(() => {
    if (!cliente) return;
    api.get(`/ped?cliente_id=${cliente}&formato=Storia`, { silent: true }).then(r => setRighe(r.data || [])).catch(() => {});
  }, 30000, !!cliente);

  // Partizione futuri/passati (oggi dal browser).
  const pad2 = (n) => String(n).padStart(2, '0');
  const _now = new Date();
  const oggiStr = `${_now.getFullYear()}-${pad2(_now.getMonth() + 1)}-${pad2(_now.getDate())}`;
  const dataOf = (r) => (r.data_pubblicazione || '').slice(0, 10);
  const isFuturo = (r) => { const d = dataOf(r); return !d || d >= oggiStr; };
  const futuri = righe.filter(isFuturo);
  const passate = righe.filter((r) => !isFuturo(r));
  const passateDaRisolvere = passate.filter((r) => r.stato_posting === 'da_risolvere').length;
  const righeSez = sezione === 'archivio' ? passate : futuri;

  // ordina per data + ora
  const ordina = (arr) => [...arr].sort((a, b) => {
    const ka = `${a.data_pubblicazione || '9999'} ${hhmm(a.ora_pubblicazione) || '99:99'}`;
    const kb = `${b.data_pubblicazione || '9999'} ${hhmm(b.ora_pubblicazione) || '99:99'}`;
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
  const sortedSez = ordina(righeSez);
  const sorted = filtroPosting ? sortedSez.filter(r => r.stato_posting === filtroPosting) : sortedSez;
  const conteggi = righeSez.reduce((a, r) => { a[r.stato_posting] = (a[r.stato_posting]||0)+1; return a; }, {});
  const pronteAuto = righeSez.filter(r => r.stato_posting === 'pronta'); // storie Auto pronte a programmare

  // Archivio per mese (più recente prima).
  const gruppiArchivio = () => {
    const map = {};
    sorted.forEach(r => { const k = dataOf(r).slice(0, 7); (map[k] = map[k] || []).push(r); });
    return Object.keys(map).sort().reverse().map(k => {
      const [y, m] = k.split('-');
      return { key: k, label: `${MESI_ARCH[parseInt(m, 10) - 1]} ${y}`, righe: map[k] };
    });
  };
  const toggleMese = (k) => setMesiChiusi(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const creaStoria = async () => {
    if (!cliente || !nuovaData) { error('Scegli cliente e data'); return; }
    setCreating(true);
    try {
      await api.post('/ped', {
        cliente_id: cliente, titolo_post: nuovoTitolo.trim() || 'Storia',
        formato: 'Storia', piattaforma: 'Instagram',
        data_pubblicazione: nuovaData, ora_pubblicazione: nuovaOra || null,
        modalita_storia: nuovaModalita, stato: 'Idea',
      });
      setNuovoTitolo('');
      success('Storia aggiunta');
      load();
    } catch { /* toast da interceptor */ } finally { setCreating(false); }
  };

  // Modifica parziale (PATCH): invia solo i campi cambiati, poi usa la riga del server
  const patchRiga = async (r, patch) => {
    setRighe(rs => rs.map(x => x.id === r.id ? { ...x, ...patch } : x));
    try {
      const res = await api.patch(`/ped/${r.id}`, patch);
      setRighe(rs => rs.map(x => x.id === r.id ? { ...x, ...res.data } : x));
    } catch { load(); }
  };

  const programma = async (r) => {
    setBusyId(r.id);
    try {
      const res = await api.post('/ped/publish', { ped_ids: [r.id] });
      const out = res.data?.results?.[0] || {};
      if (out.ok) success('Storia programmata su PostProxy');
      else error(out.error || 'Impossibile programmare');
      load();
    } catch { /* interceptor */ } finally { setBusyId(null); }
  };

  // Programma in batch tutte le storie Auto pronte.
  const programmaTutte = async () => {
    const ids = pronteAuto.map(r => r.id);
    if (!ids.length) return;
    if (!window.confirm(`Programmare su PostProxy ${ids.length} stori${ids.length===1?'a':'e'} pront${ids.length===1?'a':'e'}?`)) return;
    setPublishing(true);
    try {
      const res = await api.post('/ped/publish', { ped_ids: ids });
      const out = res.data.results || [];
      const ok = out.filter(x => x.ok).length;
      const fail = out.filter(x => !x.ok && !x.skipped).length;
      if (ok > 0) success(`Programmate ${ok}${fail ? ` · ${fail} non riuscite` : ''}`);
      else error('Nessuna storia programmata');
      load();
    } catch { /* interceptor */ } finally { setPublishing(false); }
  };

  // Storie Link/Manuale: si postano a mano → crea un reminder nel calendario
  const reminder = async (r) => {
    if (!r.data_pubblicazione) { error('Imposta prima la data'); return; }
    setBusyId(r.id);
    try {
      const res = await api.post(`/ped/${r.id}/reminder`);
      const quando = res.data?.quando ? new Date(res.data.quando).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
      success(`Reminder a calendario${quando ? ` · ${quando}` : ''}`);
    } catch { /* interceptor */ } finally { setBusyId(null); }
  };

  const cambiaModalita = (r, m) => {
    patchRiga(r, { modalita_storia: m });
    // tornando ad Auto rimuovi un eventuale reminder già in calendario
    if (m === 'Auto') api.delete(`/ped/${r.id}/reminder`).catch(() => {});
  };

  // Gestione media della storia → componente condiviso MediaManager
  const openMedia = (r) => setMediaFor(r);

  const resetPub = async (r) => {
    setBusyId(r.id);
    try { await api.delete(`/ped/${r.id}/pubblicazione`); load(); }
    finally { setBusyId(null); }
  };

  const elimina = async (r) => {
    if (!window.confirm('Eliminare questa storia dal piano?')) return;
    setBusyId(r.id);
    try { await api.delete(`/ped/${r.id}`); load(); } finally { setBusyId(null); }
  };

  const refresh = async () => {
    if (!cliente) return;
    setRefreshing(true);
    try { await api.post(`/ped/refresh_status?cliente_id=${cliente}`); load(); }
    finally { setRefreshing(false); }
  };

  const clienteSel = clienti.find(c => c.id === cliente);
  // storia con la modale media aperta, sempre allineata allo stato corrente di righe
  const modalStory = mediaFor ? (righe.find(x => x.id === mediaFor.id) || mediaFor) : null;
  // mese target del calendario in base all'orizzonte (pre-ped → mese corrente)
  const calMonth = (() => {
    const now = new Date();
    const off = orizzonte === 'mese_prossimo' ? 1 : orizzonte === 'mese_dopo' ? 2 : 0;
    return new Date(now.getFullYear(), now.getMonth() + off, 1);
  })();

  // Render di UNA storia (card) — riusato da "In programma" e da ogni mese di "Archivio".
  const renderStoria = (r) => {
    const media = Array.isArray(r.media_url) ? r.media_url : [];
    const locked = r.pub_stato === 'published' || r.pub_stato === 'scheduled';
    const busy = busyId === r.id;
    const modalita = r.modalita_storia || 'Auto';
    const isAuto = modalita === 'Auto';
    return (
      <Card key={r.id} padding="12px 14px">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Thumb */}
          <button type="button" onClick={() => openMedia(r)} title="Gestisci media"
            style={{ position: 'relative', width: 44, height: 44, borderRadius: 9, overflow: 'hidden', flexShrink: 0, padding: 0, cursor: 'pointer',
              background: 'var(--bg3)', border: `1px solid ${media.length ? 'var(--border)' : 'var(--red)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {media[0] && !/\.(mp4|mov|webm|m4v)$/i.test(media[0])
              ? <img src={media[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : media[0] ? <Film size={18} color="var(--purple)" />
              : <Plus size={18} color="var(--text-dim)" />}
            {media.length > 0
              ? <span style={{ position: 'absolute', bottom: 2, right: 2, minWidth: 15, height: 15, padding: '0 3px', borderRadius: 8, background: 'var(--green)', color: 'var(--on-accent)', fontSize: 9.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{media.length}</span>
              : <span title="Manca il media" style={{ position: 'absolute', bottom: 2, right: 2, width: 15, height: 15, borderRadius: 8, background: 'var(--red)', color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>!</span>}
          </button>

          {/* Data + ora */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <input type="date" aria-label="Data di pubblicazione" className="eden-dt"
              value={r.data_pubblicazione || ''} disabled={locked}
              onChange={e => patchRiga(r, { data_pubblicazione: e.target.value || null })}
              style={{ width: 140 }} />
            <input type="time" aria-label="Orario di pubblicazione" className="eden-dt"
              title="Vuoto = default storie 18:00"
              value={hhmm(r.ora_pubblicazione)} disabled={locked}
              onChange={e => patchRiga(r, { ora_pubblicazione: e.target.value || null })}
              style={{ width: 96 }} />
          </div>

          {/* Modalità */}
          <select value={modalita} disabled={locked} onChange={e => cambiaModalita(r, e.target.value)}
            title="Auto = PostProxy · Link/Manuale = da postare a mano (reminder)"
            className="eden-input" style={{ height: 34, width: 104, flexShrink: 0, opacity: locked ? 0.6 : 1,
              borderColor: isAuto ? 'var(--cyan-dim)' : 'var(--yellow)', color: isAuto ? 'var(--text)' : 'var(--yellow)' }}>
            <option value="Auto">Auto</option>
            <option value="Link">Link</option>
            <option value="Manuale">Manuale</option>
          </select>

          {/* Titolo + giorno */}
          <div style={{ flex: 1, minWidth: 100 }}>
            <div style={{ fontSize: 13.5, color: 'var(--text)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.titolo_post || 'Storia'}</div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{fmtData(r.data_pubblicazione)}{r.id_post ? ` · ${r.id_post}` : ''}</div>
          </div>

          {/* Stato di posting derivato (uguale per Auto e manuali) */}
          <PostingBadge stato={r.stato_posting} manca={r.posting_manca} />

          {/* Azioni */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <button onClick={() => navigate(`/ped/contenuto/${r.id}`)} title="Apri dettaglio"
              style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: 6, display: 'flex', borderRadius: 6 }}>
              <ExternalLink size={15} />
            </button>
            {isAuto ? (
              !puoPubblicare ? null : locked ? (
                <button onClick={() => resetPub(r)} disabled={busy} title="Annulla programmazione"
                  style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: 6, display: 'flex', borderRadius: 6 }}>
                  <X size={15} />
                </button>
              ) : (
                <button onClick={() => programma(r)} disabled={busy || !media.length || !r.data_pubblicazione}
                  title={!media.length ? 'Manca il media' : !r.data_pubblicazione ? 'Manca la data' : 'Programma su PostProxy'}
                  className="eden-btn is-sm" style={{ color: 'var(--cyan)' }}>
                  <Send size={13} /> Programma
                </button>
              )
            ) : (
              <button onClick={() => reminder(r)} disabled={busy || !r.data_pubblicazione}
                title={!r.data_pubblicazione ? 'Manca la data' : 'Aggiungi un reminder al calendario (post manuale)'}
                className="eden-btn is-sm" style={{ color: 'var(--yellow)' }}>
                <CalendarClock size={13} /> Reminder
              </button>
            )}
            <button onClick={() => elimina(r)} disabled={busy} title="Elimina"
              style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: 6, display: 'flex', borderRadius: 6 }}>
              <Trash2 size={15} />
            </button>
          </div>
        </div>
        {r.pub_stato === 'failed' && r.pub_error && (
          <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--red)', background: 'var(--red-dim)', padding: '6px 10px', borderRadius: 7 }}>
            {r.pub_error}
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="eden-page">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0 }}>Storie</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Ogni storia con il suo orario. Auto = esce da sola · Link/Manuale = da postare a mano.</div>
        </div>
        <button onClick={refresh} disabled={refreshing || !cliente} className="eden-btn" title="Allinea lo stato con PostProxy">
          <RefreshCw size={15} className={refreshing ? 'eden-spin' : undefined} /> Aggiorna stato
        </button>
      </div>

      {/* Selettore cliente */}
      <ClientePicker clienti={clienti} selected={cliente} onSelect={c => setCliente(c.id)} />

      {/* Sezione + mese + vista — identici al PED */}
      <div className="eden-toolbar">
        <div className="eden-seg" role="group" aria-label="Sezione">
          {[{ k: 'programma', label: 'In programma' }, { k: 'archivio', label: 'Archivio' }].map(x => (
            <button key={x.k} className={sezione === x.k ? 'is-on' : ''}
              onClick={() => { setSezione(x.k); setFiltroPosting(''); if (x.k === 'archivio') setVista('lista'); }}>
              {x.label}
              {x.k === 'archivio' && passate.length > 0 && <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600 }}>{passate.length}</span>}
              {x.k === 'archivio' && passateDaRisolvere > 0 && <span title={`${passateDaRisolvere} da risolvere`} style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--red)' }} />}
            </button>
          ))}
        </div>
        {sezione === 'programma' && vista === 'calendario' && (
          <div className="eden-scroll-row" role="group" aria-label="Mese">
            {CAL_MESI.map(o => (
              <button key={o.k} className={`eden-chip${orizzonte === o.k ? ' is-on' : ''}`} onClick={() => setOrizzonte(o.k)}>{o.label}</button>
            ))}
          </div>
        )}
        {sezione === 'programma' && (
          <div className="eden-seg" role="group" aria-label="Vista" style={{ marginLeft: 'auto' }}>
            {[{ k: 'lista', label: 'Lista', Icon: List }, { k: 'calendario', label: 'Calendario', Icon: CalendarDays }].map(({ k, label, Icon }) => (
              <button key={k} className={vista === k ? 'is-on' : ''} onClick={() => setVista(k)} aria-label={label}>
                <Icon size={15} /><span className="mobile-hide">{label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Creazione rapida (solo In programma) */}
      {sezione === 'programma' && (
      <Card padding="14px 16px" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
          Aggiungi una storia
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="date" value={nuovaData} onChange={e => setNuovaData(e.target.value)} className="eden-input"
            style={{ height: 38, width: 150 }} />
          <input type="time" value={nuovaOra} onChange={e => setNuovaOra(e.target.value)} className="eden-input" aria-label="Orario"
            style={{ height: 38, width: 120 }} />
          <select value={nuovaModalita} onChange={e => setNuovaModalita(e.target.value)} className="eden-input"
            title="Auto = PostProxy · Link/Manuale = da postare a mano (reminder)"
            style={{ height: 38, width: 120 }}>
            <option value="Auto">Auto</option>
            <option value="Link">Link</option>
            <option value="Manuale">Manuale</option>
          </select>
          <input value={nuovoTitolo} onChange={e => setNuovoTitolo(e.target.value)} placeholder="Titolo / nota (opzionale)"
            className="eden-input" style={{ height: 38, flex: '1 1 200px', minWidth: 160 }}
            onKeyDown={e => { if (e.key === 'Enter') creaStoria(); }} />
          <button onClick={creaStoria} disabled={creating || !cliente || !nuovaData} className="eden-btn is-primary" style={{ height: 38 }}>
            <Plus size={15} /> Aggiungi
          </button>
        </div>
      </Card>
      )}

      {/* Barra guardrail: stato di posting a colpo d'occhio + programma tutte le pronte (componente condiviso) */}
      <PostingGuardrail conteggi={conteggi} filtro={filtroPosting} onFiltro={setFiltroPosting}
        pronte={puoPubblicare ? pronteAuto.length : 0} onProgrammaTutte={programmaTutte} publishing={publishing} />

      {/* Lista (In programma) / Calendario / Archivio per mese — coerente col PED */}
      {loading ? (
        <ListSkeleton rows={5} />
      ) : (sezione === 'programma' && vista === 'calendario') ? (
        <PedCalendario month={calMonth} righe={sorted}
          onOpen={(r) => navigate(`/ped/contenuto/${r.id}`)}
          onMove={(r, ds) => patchRiga(r, { data_pubblicazione: ds })}
          onNew={(ds) => { setNuovaData(ds); setVista('lista'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
      ) : sorted.length === 0 ? (
        <EmptyState icon={sezione === 'archivio' ? Archive : Clock}
          title={sezione === 'archivio' ? 'Archivio vuoto' : 'Nessuna storia in programma'}
          hint={sezione === 'archivio'
            ? 'Le storie con data passata finiscono qui, raggruppate per mese.'
            : `Aggiungi la prima storia per ${clienteSel ? (clienteSel.azienda || clienteSel.nome) : 'questo cliente'} scegliendo data e orario qui sopra.`} />
      ) : sezione === 'archivio' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {gruppiArchivio().map(g => {
            const aperto = !mesiChiusi.has(g.key);
            return (
              <div key={g.key} className="eden-list">
                <button onClick={() => toggleMese(g.key)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', minHeight: 0, fontFamily: 'inherit', background: 'var(--bg2)', border: 'none', borderBottom: aperto ? '1px solid var(--border)' : 'none', cursor: 'pointer', color: 'var(--text)', textAlign: 'left' }}>
                  <ChevronDown size={15} style={{ flexShrink: 0, transform: aperto ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s', color: 'var(--text-muted)' }} />
                  <span style={{ fontSize: 13.5, fontWeight: 700 }}>{g.label}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>· {g.righe.length} stori{g.righe.length === 1 ? 'a' : 'e'}</span>
                </button>
                {aperto && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12 }}>
                    {g.righe.map(renderStoria)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sorted.map(renderStoria)}
        </div>
      )}

      {/* Gestione media della storia (componente condiviso) */}
      <MediaManager row={modalStory} clienteId={cliente} onPatch={patchRiga} onClose={() => setMediaFor(null)} />
    </div>
  );
}
